import { inject, Injectable } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';

import { PriorityType, PriorityTypeDetails } from '../../../core/enum/EPriorityType';
import { EntryPostBody, IBranchResponse } from '../../../core/interfaces';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class WaitingListFormService {
  readonly #fb = inject(FormBuilder);
  readonly #fbnn = this.#fb.nonNullable;
  readonly #translate = inject(TranslateService);

  readonly form = this.#fb.group({
    name: this.#fbnn.control('', [Validators.required, Validators.maxLength(50)]),
    phone: this.#fbnn.control('', Validators.required),
    count: this.#fbnn.control('', [Validators.required, Validators.min(1), Validators.max(99)]),
    priorities: this.#fb.group({
      priority: this.#fb.control<boolean | null>(null, Validators.required),
      priorityTypes: this.#fbnn.control<PriorityType[]>([]),
      otherPriorityLabel: this.#fb.control<string | null>(null),
    }),
    pet: this.#fb.control<boolean | null>(null, Validators.required),
    noPhone: this.#fbnn.control(false),
    commentary: this.#fb.control(null),
    origin: this.#fbnn.control('Customer'),
  });

  initializeForm(): void {
    this.bindFormValidations();
  }

  private bindFormValidations(): void {
    const { priority, priorityTypes, otherPriorityLabel } = this.form.controls.priorities.controls;
    const controls = [priority, priorityTypes, otherPriorityLabel];

    priority.valueChanges.subscribe((value) => {
      value
        ? priorityTypes.addValidators(Validators.required)
        : priorityTypes.removeValidators(Validators.required);

      otherPriorityLabel.setValue(null);
      priorityTypes.setValue([]);

      controls.forEach((c) => c.updateValueAndValidity({ emitEvent: false }));
    });

    priorityTypes.valueChanges.subscribe((value) => {
      value.includes(PriorityType.Other)
        ? otherPriorityLabel.addValidators(Validators.required)
        : otherPriorityLabel.removeValidators(Validators.required);

      otherPriorityLabel.updateValueAndValidity({ emitEvent: false });
    });
  }

  mapPriorityTypesToBody(): string | null {
    const priorityTypes = this.form.getRawValue().priorities.priorityTypes;
    return priorityTypes.length > 0
      ? priorityTypes.map((t) => PriorityTypeDetails[t].value).join(', ')
      : null;
  }

  mapPriorityTypesToDescription(): string | null {
    const priorityTypes = this.form.getRawValue().priorities.priorityTypes;
    return priorityTypes.length > 0
      ? priorityTypes
          .map((t) =>
            this.#translate.instant(PriorityTypeDetails[t].labelKey)
          )
          .join(', ')
      : null;
  }

  getWaitingListEntryBody(branch: IBranchResponse): EntryPostBody {
    const { priorities, pet, count, phone, ...formValue } = this.form.getRawValue();

    const body: EntryPostBody = {
      branchId: branch.id,
      entryData: {
        ...formValue,
        ...priorities,
        phone: phone.substring(1), // Remove the '+' suffix
        count: Number(count),
        pet: pet ?? false,
        origin: 'Customer',
        priorityType: this.mapPriorityTypesToBody(),
        branchCode: branch.code,
      },
    };

    return body;
  }
}
